// Organization.styles.ts
import { corDeTextoAcessivel } from "../../../../global/tokens";
import styled, { keyframes } from "styled-components";

// ===== ANIMAÇÕES =====
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }

  &:hover {
    scrollbar-width: auto;
  }
  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-32);
  flex-wrap: wrap;
  gap: var(--sp-20);
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-24);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-12);
`;

export const AvatarWrapper = styled.div<{ $isUploading?: boolean }>`
  position: relative;
  opacity: ${(props) => (props.$isUploading ? 0.6 : 1)};
  transition: opacity 0.3s ease;
`;

export const AvatarHint = styled.span`
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.label};
  font-style: italic;
  animation: ${fadeIn} 0.3s ease;
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 50%;
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.2s ease;
`;

export const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);
  color: white;
  font-size: var(--fs-sm);
  font-weight: 600;
  text-align: center;
  padding: var(--sp-16);
`;

export const LoadingSpinner = styled.div`
  width: calc(var(--u) * 24);
  height: calc(var(--u) * 24);
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const LoadingSubtext = styled.div`
  font-size: var(--fs-2xs);
  font-weight: 400;
  opacity: 0.9;
`;

export const OrganizationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const OrganizationTitle = styled.h1`
  font-size: var(--fs-4xl);
  font-weight: 700;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
`;

export const OrganizationSubtitle = styled.p`
  color: ${(props) => props.theme.colors.label};
  margin: 0;
  font-size: var(--fs-base);
`;

// ===== BANNER DE SUCESSO =====
export const SuccessBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-12);
  padding: var(--sp-16);
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8f4 100%);
  border: 1px solid #4caf50;
  border-radius: 10px;
  margin-bottom: var(--sp-24);
  animation: ${slideIn} 0.4s ease;

  > span {
    color: #2e7d32;
    font-size: calc(var(--u) * 22);
    flex-shrink: 0;
    margin-top: var(--sp-2);
  }
`;

export const SuccessBannerTitle = styled.div`
  font-weight: 600;
  font-size: var(--fs-md);
  color: #1b5e20;
  margin-bottom: var(--sp-4);
`;

export const SuccessBannerText = styled.div`
  font-size: var(--fs-sm);
  color: #2e7d32;
  line-height: 1.5;
`;

// ===== MÉTRICAS =====
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 240), 1fr));
  gap: var(--sp-20);
  margin-bottom: var(--sp-32);
  animation: ${scaleIn} 0.5s ease;
`;

export const MetricCard = styled.div`
  padding: var(--sp-24);
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const MetricIcon = styled.div`
  font-size: var(--fs-4xl);
  margin-bottom: var(--sp-8);
`;

export const MetricValue = styled.div<{ $color?: string }>`
  font-size: var(--fs-4xl);
  font-weight: 700;
  color: ${(props) =>
    corDeTextoAcessivel(props.$color) || props.theme.colors.selection};
  margin-bottom: var(--sp-8);
`;

export const MetricLabel = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
`;

// ===== SEÇÕES =====
export const Section = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: var(--sp-24);
  animation: ${slideIn} 0.4s ease;
`;

export const SectionHeader = styled.div`
  padding: var(--sp-20) var(--sp-24);
  background-color: ${(props) => props.theme.colors.background + "80"};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-12);
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-8);
`;

export const SectionContent = styled.div`
  padding: var(--sp-24);
`;

export const StatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-6);
  padding: var(--sp-6) var(--sp-16);
  border-radius: 20px;
  font-size: var(--fs-md);
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.$isActive ? "#155724" : "#721c24")};
  border: 1px solid ${(props) => (props.$isActive ? "#c3e6cb" : "#f5c6cb")};
`;

// ===== FORMULÁRIO =====
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-20);
  animation: ${slideIn} 0.4s ease;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--sp-16);
  }
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  width: 100%;
`;

export const Label = styled.label<{ $required?: boolean }>`
  font-weight: 600;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-6);

  ${(props) =>
    props.$required &&
    `
    &::after {
      content: "*";
      color: #f44336;
      margin-left: var(--sp-2);
    }
  `}
`;

export const Input = styled.input<{
  $hasError?: boolean;
  $isReadOnly?: boolean;
}>`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 14);
  border: 2px solid
    ${(props) =>
      props.$hasError
        ? "#f44336"
        : props.$isReadOnly
          ? props.theme.colors.border + "60"
          : props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) =>
    props.$isReadOnly
      ? props.theme.colors.background + "40"
      : props.theme.colors.background};
  color: ${(props) =>
    props.$isReadOnly
      ? props.theme.colors.label
      : props.theme.colors.text};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.$hasError ? "#f44336" : props.theme.colors.selection};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.$hasError
          ? "rgba(244, 67, 54, 0.1)"
          : props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 14);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  transition: all 0.2s ease;
  min-height: calc(var(--u) * 100);

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }
`;

export const ErrorText = styled.span`
  font-size: var(--fs-xs);
  color: #f44336;
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-top: calc(var(--u) * -2);
  animation: ${fadeIn} 0.2s ease;

  &::before {
    content: "⚠";
    font-size: var(--fs-md);
  }
`;

// ===== INFO GRID (MODO VISUALIZAÇÃO) =====
export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-20);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const InfoLabel = styled.div`
  font-weight: 600;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  display: flex;
  align-items: center;
  gap: var(--sp-6);
`;

export const InfoValue = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.text};
  line-height: 1.5;
`;

// ===== CONFIGURAÇÕES =====
export const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 300), 1fr));
  gap: var(--sp-32);
`;

export const ConfigGroupTitle = styled.h4`
  margin: 0 0 var(--sp-16) 0;
  font-size: var(--fs-base);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const ConfigItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--sp-16);
  padding-bottom: var(--sp-16);
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
`;

export const ConfigItemTitle = styled.div`
  font-weight: 500;
  margin-bottom: var(--sp-4);
  color: ${(props) => props.theme.colors.text};
`;

export const ConfigItemSubtitle = styled.div`
  color: ${(props) => props.theme.colors.label};
  font-size: var(--fs-md);
`;

// ===== TOGGLE SWITCH =====
export const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: calc(var(--u) * 44);
  height: calc(var(--u) * 24);

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
      background-color: ${(props) => props.theme.colors.selection};
    }

    &:checked + span > span {
      transform: translateX(20px);
    }
  }
`;

export const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
`;

export const ToggleButton = styled.span`
  position: absolute;
  height: calc(var(--u) * 18);
  width: calc(var(--u) * 18);
  left: calc(var(--u) * 3);
  bottom: calc(var(--u) * 3);
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
`;

// ===== INTEGRAÇÕES =====
export const IntegrationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-12);
  font-weight: 500;
  color: ${(props) => props.theme.colors.text};
`;

export const IntegrationBadge = styled.span<{ $status: "active" | "pending" }>`
  padding: var(--sp-4) var(--sp-12);
  border-radius: 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  background-color: ${(props) =>
    props.$status === "active" ? "#d4edda" : "#fff3cd"};
  color: ${(props) => (props.$status === "active" ? "#155724" : "#856404")};
`;

// ===== BOTÕES =====
export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-12);
  margin-top: var(--sp-24);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) calc(var(--u) * 28);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-8);
  min-width: calc(var(--u) * 160);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) calc(var(--u) * 28);
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--fs-md);
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-8);
  min-width: calc(var(--u) * 120);

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.background};
    border-color: ${(props) => props.theme.colors.text + "40"};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ButtonSpinner = styled.div`
  width: calc(var(--u) * 16);
  height: calc(var(--u) * 16);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

// ===== ÍCONES =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-lg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

// Exportação padrão
export default {
  FieldsContainer,
  Header,
  HeaderContent,
  LogoSection,
  AvatarWrapper,
  AvatarHint,
  LoadingOverlay,
  LoadingContent,
  LoadingSpinner,
  LoadingSubtext,
  OrganizationInfo,
  OrganizationTitle,
  OrganizationSubtitle,
  SuccessBanner,
  SuccessBannerTitle,
  SuccessBannerText,
  MetricsGrid,
  MetricCard,
  MetricIcon,
  MetricValue,
  MetricLabel,
  Section,
  SectionHeader,
  SectionTitle,
  SectionContent,
  StatusBadge,
  FormGrid,
  InputWrapper,
  Label,
  Input,
  TextArea,
  ErrorText,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ConfigGrid,
  ConfigGroupTitle,
  ConfigItem,
  ConfigItemTitle,
  ConfigItemSubtitle,
  Toggle,
  ToggleSlider,
  ToggleButton,
  IntegrationItem,
  IntegrationBadge,
  ButtonWrapper,
  Button,
  SecondaryButton,
  ButtonSpinner,
  MaterialIcons,
};
